// src/Views/WorkAroundPage.js — WorkAround product landing page
import React from 'react';
import { Helmet } from 'react-helmet';

const styles = {
  page: {
    backgroundColor: 'var(--color-bg-canvas)',
    color: 'var(--color-text-on-dark)',
    minHeight: '100vh',
  },
  hero: {
    padding: 'calc(var(--space-16) + 60px) var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-block',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-warning)',
    border: '1px solid var(--color-warning)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 10px',
    marginBottom: 'var(--space-4)',
    letterSpacing: 'var(--letter-spacing-label)',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 'var(--font-size-4xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  tagline: {
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  section: {
    padding: '0 var(--space-6) var(--space-12)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-4)',
    lineHeight: 'var(--line-height-tight)',
  },
  paragraph: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-on-dark-body)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-4)',
  },
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 'var(--space-6)',
    marginTop: 'var(--space-6)',
  },
  stepCard: {
    backgroundColor: 'var(--color-bg-surface-2)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-6)',
    border: '1px solid var(--color-border-subtle)',
  },
  stepNumber: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-primary)',
    marginBottom: 'var(--space-2)',
  },
  stepTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-on-dark)',
    marginBottom: 'var(--space-2)',
  },
  stepDesc: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  statusSection: {
    padding: 'var(--space-8) var(--space-6)',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'var(--color-bg-surface)',
    borderRadius: 'var(--radius-lg)',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  statusWrapper: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 var(--space-6) var(--space-12)',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-3)',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '7px',
    flexShrink: 0,
  },
  statusText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    margin: 0,
  },
  ctaSection: {
    padding: 'var(--space-12) var(--space-6) var(--space-16)',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    borderTop: '1px solid var(--color-border-subtle)',
  },
  ctaText: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-on-dark-secondary)',
    lineHeight: 'var(--line-height-relaxed)',
    marginBottom: 'var(--space-6)',
  },
  ctaLink: {
    display: 'inline-block',
    padding: 'var(--space-3) var(--space-8)',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-dark)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    transition: 'background-color var(--transition-base), box-shadow var(--transition-base)',
  },
};

const WorkAroundPage = () => {
  return (
    <div style={styles.page}>
      <Helmet>
        <title>WorkAround — Solomon Foshko | Design by Dialogue</title>
        <meta name="description" content="A Chrome extension that captures how you work in the browser and exports those workflows as instructions for AI agents. No code. No APIs." />
      </Helmet>

      {/* Hero */}
      <div style={styles.hero}>
        <span style={styles.badge}>In Progress</span>
        <h1 style={styles.title}>WorkAround</h1>
        <p style={styles.tagline}>
          A Chrome extension that captures how you work in the browser and
          exports those workflows as instructions for AI agents. No code. No APIs.
          You do it once, agents do it from there.
        </p>
      </div>

      {/* Problem */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>The Problem</h2>
        <p style={styles.paragraph}>
          Every browser automation tool starts from the developer's side — write a script, configure
          a pipeline, manage API keys. But the person who knows the workflow isn't a developer. They're
          the person doing the work every day in the browser, clicking through the same steps over
          and over.
        </p>
        <p style={styles.paragraph}>
          WorkAround starts from their side. Show it how you do the work. It captures the
          instructions. Any AI agent can execute them.
        </p>
      </div>

      {/* How It Works */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>How It Works</h2>
        <div style={styles.stepsGrid}>
          <div style={styles.stepCard}>
            <div style={styles.stepNumber}>1</div>
            <h3 style={styles.stepTitle}>Record</h3>
            <p style={styles.stepDesc}>
              Click record in the browser sidebar. Do the work — navigate, click, type, copy.
              WorkAround watches and captures every step as a semantic instruction.
            </p>
          </div>
          <div style={styles.stepCard}>
            <div style={styles.stepNumber}>2</div>
            <h3 style={styles.stepTitle}>Capture</h3>
            <p style={styles.stepDesc}>
              The extension normalizes your actions into structured, human-readable instructions
              with confidence-scored selectors and fallback strategies.
            </p>
          </div>
          <div style={styles.stepCard}>
            <div style={styles.stepNumber}>3</div>
            <h3 style={styles.stepTitle}>Execute</h3>
            <p style={styles.stepDesc}>
              Any AI browser agent can replay the captured instructions — Browser-Use, Claude
              Computer Use, Stagehand, or WorkAround's own execution engine.
            </p>
          </div>
        </div>
      </div>

      {/* Current Status */}
      <div style={styles.statusWrapper}>
        <div style={styles.statusSection}>
          <h2 style={{...styles.sectionTitle, marginBottom: 'var(--space-4)'}}>Current Status</h2>
          <p style={{...styles.paragraph, marginBottom: 'var(--space-4)'}}>
            WorkAround is in active development. Here's an honest picture of where it stands:
          </p>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>Chrome extension (MV3) with recording and workflow execution</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>Semantic selector generation with fallback strategies</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-success)'}} />
            <p style={styles.statusText}>Multi-tab recording, SPA detection, credential sanitization</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-warning)'}} />
            <p style={styles.statusText}>LinkedIn posting workflow — in development, not yet reliable</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-warning)'}} />
            <p style={styles.statusText}>Full replay verification against live sites</p>
          </div>
          <div style={styles.statusItem}>
            <div style={{...styles.statusDot, backgroundColor: 'var(--color-text-on-dark-muted)'}} />
            <p style={styles.statusText}>Not yet published to Chrome Web Store — sideloaded only</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={styles.ctaSection}>
        <p style={styles.ctaText}>
          WorkAround is being built in public. Follow the build on LinkedIn for progress updates,
          honest failures, and the real story of shipping browser automation that works.
        </p>
        <a
          href="https://www.linkedin.com/in/solomonfoshko/"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.ctaLink}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--color-primary-dark)';
            e.target.style.boxShadow = 'var(--shadow-dark-button)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'var(--color-primary)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Follow the build on LinkedIn
        </a>
      </div>
    </div>
  );
};

export default WorkAroundPage;
